import React from 'react';
import { Alert, Portal, Snackbar } from '@mui/material';

/** Purpose: Choose severity colors for the active theme. Why: Success, warning, and error feedback must remain distinguishable. */
const getSeverityTheme = (severity, isDark) => {
  switch (severity) {
    case 'success':
      return {
        bg: isDark
          ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.88) 0%, rgba(6, 78, 59, 0.45) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(240, 253, 244, 0.85) 100%)',
        border: isDark ? 'rgba(52, 211, 153, 0.35)' : 'rgba(16, 185, 129, 0.32)',
        icon: isDark ? '#34d399' : '#10b981',
        boxShadow: isDark
          ? '0 16px 36px -4px rgba(0, 0, 0, 0.55), 0 0 16px rgba(16, 185, 129, 0.15)'
          : '0 14px 32px -4px rgba(15, 23, 42, 0.1), 0 4px 12px -2px rgba(16, 185, 129, 0.08)',
      };
    case 'error':
      return {
        bg: isDark
          ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.88) 0%, rgba(127, 29, 29, 0.45) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(254, 242, 242, 0.85) 100%)',
        border: isDark ? 'rgba(248, 113, 113, 0.35)' : 'rgba(239, 68, 68, 0.32)',
        icon: isDark ? '#f87171' : '#ef4444',
        boxShadow: isDark
          ? '0 16px 36px -4px rgba(0, 0, 0, 0.55), 0 0 16px rgba(239, 68, 68, 0.15)'
          : '0 14px 32px -4px rgba(15, 23, 42, 0.1), 0 4px 12px -2px rgba(239, 68, 68, 0.08)',
      };
    case 'warning':
      return {
        bg: isDark
          ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.88) 0%, rgba(120, 53, 15, 0.45) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(255, 251, 235, 0.85) 100%)',
        border: isDark ? 'rgba(251, 191, 36, 0.35)' : 'rgba(245, 158, 11, 0.32)',
        icon: isDark ? '#fbbf24' : '#f59e0b',
        boxShadow: isDark
          ? '0 16px 36px -4px rgba(0, 0, 0, 0.55), 0 0 16px rgba(245, 158, 11, 0.15)'
          : '0 14px 32px -4px rgba(15, 23, 42, 0.1), 0 4px 12px -2px rgba(245, 158, 11, 0.08)',
      };
    default:
      return {
        bg: isDark
          ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.88) 0%, rgba(30, 58, 138, 0.45) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(239, 246, 255, 0.85) 100%)',
        border: isDark ? 'rgba(96, 165, 250, 0.35)' : 'rgba(59, 130, 246, 0.32)',
        icon: isDark ? '#60a5fa' : '#2563eb',
        boxShadow: isDark
          ? '0 16px 36px -4px rgba(0, 0, 0, 0.55), 0 0 16px rgba(59, 130, 246, 0.15)'
          : '0 14px 32px -4px rgba(15, 23, 42, 0.1), 0 4px 12px -2px rgba(59, 130, 246, 0.08)',
      };
  }
};

/** Purpose: display a dismissible transient status message. Why: async UI actions need visible feedback without permanently occupying the page. */
export default function StatusNotification({ message, onClose, autoHideDuration = 5000 }) {
  if (!message) return null;
  const severity = message.severity || message.type || 'info';

  /** Purpose: Handle dismissal while ignoring incidental clickaway. Why: Feedback should not disappear just because the user clicks elsewhere. */
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    if (onClose) onClose();
  };

  return (
    <Portal>
      <Snackbar
        open={Boolean(message)}
        autoHideDuration={autoHideDuration}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          /** Purpose: Place notifications above modal content. Why: Feedback from modal actions must remain visible. */
          zIndex: (theme) => theme.zIndex.modal + 200,
          maxWidth: 'calc(100vw - 32px)',
          top: { xs: '16px !important', sm: '24px !important' },
        }}
      >
        <Alert
          severity={severity}
          onClose={onClose}
          sx={(theme) => {
            const isDark = theme.palette.mode === 'dark';
            const style = getSeverityTheme(severity, isDark);
            return {
              width: '100%',
              minWidth: { sm: '320px' },
              maxWidth: '520px',
              background: style.bg,
              backdropFilter: 'blur(16px) saturate(180%)',
              WebkitBackdropFilter: 'blur(16px) saturate(180%)',
              border: `1px solid ${style.border}`,
              boxShadow: style.boxShadow,
              borderRadius: '14px',
              fontSize: '0.88rem',
              fontWeight: 600,
              alignItems: 'center',
              py: 0.8,
              px: 2,
              overflowWrap: 'anywhere',
              maxHeight: '40vh',
              overflowY: 'auto',
              color: isDark ? '#f8fafc' : '#0f172a',
              '& .MuiAlert-icon': {
                color: style.icon,
                mr: 1.5,
                fontSize: '1.35rem',
                alignSelf: 'center',
              },
              '& .MuiAlert-message': {
                padding: '4px 0',
                color: isDark ? '#f8fafc' : '#0f172a',
                letterSpacing: '-0.01em',
              },
              '& .MuiAlert-action': {
                pt: 0,
                pb: 0,
                mr: -0.5,
                alignSelf: 'center',
                '& .MuiIconButton-root': {
                  color: isDark ? '#94a3b8' : '#64748b',
                  p: 0.5,
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
                    color: isDark ? '#f8fafc' : '#0f172a',
                  },
                },
              },
            };
          }}
        >
          {message.text || message}
        </Alert>
      </Snackbar>
    </Portal>
  );
}
